import { useMutation, type MutateOptions, type MutationFunctionContext, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { authenticatedCacheGeneration, requireAuthenticatedGeneration } from '@/shared/lib/authenticated-session';
import { captureModelCommandContext, requireModelCommandContext, type ModelCommandContext } from '@/shared/lib/model-command-context';

type OwnedMutationOptions<TData, TError, TVariables, TContext> =
  Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> & {
    mutationFn?: (variables: TVariables, context: MutationFunctionContext & {
      invocationOwner: ModelCommandContext;
    }) => Promise<TData>;
  };

/** Capture the invocation before TanStack can pause it or update its observer
 * options. Callbacks belong to that invocation, never a later render/session.
 * The envelope stays local; the original function receives its original DTO. */
export function useOwnedMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  modelId: string | null | undefined,
  options: OwnedMutationOptions<TData, TError, TVariables, TContext>,
  requireModelOwner = false,
): UseMutationResult<TData, TError, TVariables, TContext> {
  type Invocation = {
    variables: TVariables;
    owner: ModelCommandContext;
    handlers: typeof options;
  };
  const currentSession = (invocation: Invocation) =>
    invocation?.owner?.authGeneration === authenticatedCacheGeneration();
  const requireOwner = (invocation: Invocation) => {
    if (!invocation?.owner || !Number.isSafeInteger(invocation.owner.authGeneration)) {
      throw new Error('MODEL_COMMAND_CONTEXT_REQUIRED');
    }
    requireAuthenticatedGeneration(invocation.owner.authGeneration);
    if (requireModelOwner && invocation.owner.modelId) requireModelCommandContext(invocation.owner, modelId);
  };
  const mutation = useMutation<TData, TError, Invocation, TContext>({
    ...options,
    mutationFn: async (invocation, context) => {
      requireOwner(invocation);
      if (!invocation.handlers.mutationFn) throw new Error('MODEL_COMMAND_INVOCATION_MISSING');
      const result = await invocation.handlers.mutationFn(invocation.variables, {
        ...context, invocationOwner: invocation.owner,
      });
      requireAuthenticatedGeneration(invocation.owner.authGeneration);
      return result;
    },
    onMutate: options.onMutate ? async (invocation, context) => {
      requireOwner(invocation);
      const handler = invocation.handlers.onMutate;
      if (!handler) throw new Error('MODEL_COMMAND_ON_MUTATE_MISSING');
      const result = await handler(invocation.variables, context);
      requireAuthenticatedGeneration(invocation.owner.authGeneration);
      return result;
    } : undefined,
    onSuccess: (data, invocation, result, context) => {
      if (currentSession(invocation)) return invocation.handlers.onSuccess?.(data, invocation.variables, result, context);
    },
    onError: (error, invocation, result, context) => {
      if (currentSession(invocation)) return invocation.handlers.onError?.(error, invocation.variables, result, context);
    },
    onSettled: (data, error, invocation, result, context) => {
      if (currentSession(invocation)) return invocation.handlers.onSettled?.(data, error, invocation.variables, result, context);
    },
  });
  const capture = (variables: TVariables): Invocation => {
    const earlier = variables && typeof variables === 'object' && 'commandContext' in variables
      ? (variables as { commandContext?: ModelCommandContext }).commandContext : undefined;
    return { variables, owner: earlier ?? captureModelCommandContext(modelId), handlers: options };
  };
  const callbacks = (invocation: Invocation, supplied?: MutateOptions<TData, TError, TVariables, TContext>):
    MutateOptions<TData, TError, Invocation, TContext> | undefined => supplied ? {
      onSuccess: (data, _invocation, result, context) => {
        if (currentSession(invocation)) supplied.onSuccess?.(data, invocation.variables, result, context);
      },
      onError: (error, _invocation, result, context) => {
        if (currentSession(invocation)) supplied.onError?.(error, invocation.variables, result, context);
      },
      onSettled: (data, error, _invocation, result, context) => {
        if (currentSession(invocation)) supplied.onSettled?.(data, error, invocation.variables, result, context);
      },
    } : undefined;
  return {
    ...mutation,
    variables: mutation.variables?.variables,
    mutate: (variables, supplied) => {
      const invocation = capture(variables);
      mutation.mutate(invocation, callbacks(invocation, supplied));
    },
    mutateAsync: (variables, supplied) => {
      const invocation = capture(variables);
      return mutation.mutateAsync(invocation, callbacks(invocation, supplied));
    },
  } as UseMutationResult<TData, TError, TVariables, TContext>;
}
